import { JQueryInput } from "./types";
import { mobileAccess } from "./skyward.base.global";
import { registerLoadDOMCallback } from "./skyward.ui.window";
import debounce from "./skyward.ui.debounce";

const MAXWIDTH = 234;

const selectors =
{
    validContainers: ".workflow, .messageBox, .templateView, .menuContentArea, .js-fieldset, .dynamicNavigatorContent, .workflowTemplateEditingInner, .js-printContainer",
    labels: "label.title, label.unstyledTitle, .js-title-margin",
    otherElements: "label.help, .example, .addTitleMargin",
    contentAreas: ".currentRecordContent, .workflowContent, .pseudoContentArea, .js-alignTitlesContainer",

    ignoreAlignmentContainer: ".js-ignoreAlignmentContainer,.gridContentArea",
    noJsAlignment: ".noJsAlignment,.gridContentArea *:not(.title)",
};

const classes =
{
    twoLines: "twoLines",
    inlineLabel: "inlineLabel",
};

/**
 * Align all titles in a newly-loaded DOM component.
 * 
 * If selector is not a valid container, the nearest parent that *is* a valid container will be found, and its titles will be aligned.
 * Adds a left margin equal to the widest title on elements marked .addTitleMargin, label.help, or .example.
 * Sets "twoLine" mode on titles.
 * 
 * @param selector Selector for a JQuery container to align titles in
 */
export const alignTitles = function (selector: JQueryInput)
{
    const container = $(selector);
    let contentContainer = container.closest(selectors.validContainers).not(selectors.ignoreAlignmentContainer);
    if (contentContainer.length === 0)
    {
        contentContainer = container.find(selectors.validContainers).not(selectors.ignoreAlignmentContainer);
        if (contentContainer.length === 0)
        {
            return;
        }
    }

    const titles: JQuery<HTMLElement> = contentContainer.find(selectors.labels).not(selectors.noJsAlignment);
    if (mobileAccess)
    {
        setTitleCSSMobile(titles);
    }
    else
    {
        setTitleCSSDesktop(titles);
    }

    //With a details panel open there could be multiple "currentRecords"
    const onlyContainersLeftHiddenFilter = function (this: HTMLElement) { return $(this).css("visibility") === "hidden"; };
    $(selectors.contentAreas).filter(onlyContainersLeftHiddenFilter).css("visibility", "inherit");

    function setTitleCSSDesktop(titles: JQuery<HTMLElement>)
    {
        if (titles.length === 0)
        {
            return;
        }

        const widths = getWidthsAndSetTwoLineMode(titles);
        const maxTitleWidth = Math.max.apply(undefined, Array.from(widths.values()));
        widths.forEach(function (value, key)
        {
            const margin = maxTitleWidth - value;
            if (margin > 0)
            {
                $(key).css("margin-left", margin);
            }
            else
            {
                $(key).css("margin-left", '');
            }
        });

        contentContainer.find(selectors.otherElements).not(selectors.noJsAlignment).css("margin-left", maxTitleWidth + "px");
    }

    function setTitleCSSMobile(titles: JQuery<HTMLElement>)
    {
        titles.each(function ()
        {
            const currentLabel = $(this);
            const nextElement = currentLabel.next();
            if (nextElement.is(':checkbox'))
            {
                currentLabel.addClass(classes.inlineLabel);
            }
            else 
            {
                currentLabel.removeAttr('for');
                if (nextElement.is('table.inlineBlock'))
                {
                    nextElement.css('margin-left', '1.5rem');
                }
            }
            currentLabel.addClass(classes.twoLines);
            currentLabel.css("margin-left", 0);
        });
    }
};

/**
 * Apply .twoLines to any elements that are at least as wide as MAXWIDTH, and return measured widths
 * @param elements The elements to measure
 * @returns A map of elements to their measured widths
 */
export const getWidthsAndSetTwoLineMode = function (elements: JQuery<HTMLElement>)
{
    const widths = new Map<HTMLElement, number>();

    elements.each(function ()
    {
        const element = $(this);
        const width = getElementWidth(element);

        if (!element.hasClass(classes.twoLines) && width >= MAXWIDTH)
        {
            const originalHeight = element.height();
            element.addClass(classes.twoLines);
            const heightWithTwoLinesClass = element.height();
            if (originalHeight === heightWithTwoLinesClass)
            {
                element.removeClass(classes.twoLines);
            }
            widths.set(this, MAXWIDTH);
        }
        else
        {
            widths.set(this, width);
        }
    });

    return widths;

    function getElementWidth(element: JQuery<HTMLElement>)
    {
        const width = element.is(":visible") ? element.outerWidth()! : getElementWidthFromClone(element);

        // Chrome miscalculates widths by small fractions at certain zoom levels.  Here we implement the same solution as JQuery (https://github.com/jquery/jquery/pull/3872).
        return Math.round(width);
    }

    function getElementWidthFromClone(element: JQuery<HTMLElement>)
    {
        const clone = element.clone().css({ position: "absolute", display: "block", visibility: "hidden" }).appendTo(document.body);
        const width = clone.outerWidth()!;

        clone.remove();

        return width;
    }
};

// Use weak imports to ensure we don't depend on translationDetector in bundles that don't have it, such as
// htmlviewer and print. 
import(/* webpackMode: "weak" */ "./skyward.ui.translationDetector").then(({ onTranslated }) =>
{
    onTranslated.registerCallback(() =>
    {
        const debouncedAlign = debounce(() =>
        {
            alignTitles(document.body);
            observer.disconnect();
        }, 250);
        const observer = new MutationObserver(() => debouncedAlign());
        $(selectors.validContainers).each(function ()
        {
            observer.observe(this, {
                characterData: true,
                subtree: true,
                childList: true
            });
        })
    });
})
.catch(() => { /* we don't care if the import is unavailable */ });

registerLoadDOMCallback(alignTitles);
